import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Upload, Download, Trash2, Paperclip, Clock } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { listAttachments, uploadAttachment, deleteAttachment, fetchAttachmentBlob } from './materialApi';

/**
 * MaterialAttachments — PDF-Anhänge am Materialstamm. Hinzufügen per Button ODER
 * Drag-and-Drop (mehrere PDFs).
 *  - Gespeichertes Material (materialId): Upload sofort an den Server.
 *  - Neues Material (kein materialId): Dateien werden VORGEMERKT (staged) und nach
 *    dem Anlegen automatisch hochgeladen (Parent steuert das via onStaged).
 *
 * Props: user, materialId|null, staged [File], onStaged(files), showSuccess, showError
 */
const MaterialAttachments = ({ user, materialId, staged = [], onStaged, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const cardCls = isDarkMode ? 'bg-gray-900/30 border border-white/10 backdrop-blur-xl' : 'bg-white border border-slate-200';

  // Verhindert global, dass ein NEBEN die Drop-Zone gezogenes PDF den Browser
  // navigiert/öffnet (Standardverhalten). Drops auf die Zone werden weiter normal verarbeitet.
  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => { window.removeEventListener('dragover', prevent); window.removeEventListener('drop', prevent); };
  }, []);

  const load = useCallback(async () => {
    if (!materialId) return;
    try {
      const res = await listAttachments(user, materialId);
      setItems(res.attachments || []);
    } catch { showError?.(t('material.attLoadError')); }
  }, [user, materialId, showError, t]);

  useEffect(() => { load(); }, [load]);

  const pickPdfs = (fileList) => {
    const files = Array.from(fileList || []);
    const pdfs = files.filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    const rejected = files.length - pdfs.length;
    if (rejected > 0 && !pdfs.length) showError?.(t('material.attOnlyPdf'));
    return pdfs;
  };

  // Server-Upload (nur bei gespeichertem Material)
  const uploadNow = useCallback(async (pdfs) => {
    if (!pdfs.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const f of pdfs) {
        try { await uploadAttachment(user, materialId, f); ok += 1; }
        catch (err) { showError?.(err.message || t('material.attUploadError')); }
      }
      if (ok) showSuccess?.(t('material.attUploaded'));
      await load();
    } finally { setBusy(false); }
  }, [user, materialId, showSuccess, showError, t, load]);

  // Vormerken (neues, noch nicht gespeichertes Material)
  const stage = (pdfs) => { if (pdfs.length) onStaged?.([...staged, ...pdfs]); };

  const handleFiles = (fileList) => {
    const pdfs = pickPdfs(fileList);
    if (!pdfs.length) return;
    if (materialId) uploadNow(pdfs); else stage(pdfs);
  };

  const onPick = (e) => { handleFiles(e.target.files); e.target.value = ''; };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (!busy) handleFiles(e.dataTransfer?.files); };

  const onDownload = async (a) => {
    try {
      const blob = await fetchAttachmentBlob(user, materialId, a.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { showError?.(t('material.attLoadError')); }
  };

  const onDelete = async (a) => {
    if (!window.confirm(t('material.attConfirmDelete'))) return;
    try {
      await deleteAttachment(user, materialId, a.id);
      showSuccess?.(t('material.attDeleted'));
      load();
    } catch { showError?.(t('material.attDeleteError')); }
  };

  const removeStaged = (idx) => onStaged?.(staged.filter((_, i) => i !== idx));
  const kb = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  const count = materialId ? items.length : staged.length;

  return (
    <div className={`rounded-2xl ${cardCls} overflow-hidden`}>
      <div className={`flex items-center justify-between px-4 py-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
        <span className="text-sm font-semibold flex items-center gap-2">
          <Paperclip className="w-4 h-4" /> {t('material.attachments')}
          <span className={`text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{count}</span>
        </span>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={onPick} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-600 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {t('material.attAdd')}
        </button>
      </div>

      <div
        onDragOver={onDragOver} onDragEnter={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
        className={`mx-4 mb-4 rounded-xl border-2 border-dashed transition-colors ${
          dragOver ? 'border-emerald-500 bg-emerald-500/5' : (isDarkMode ? 'border-white/10' : 'border-slate-200')
        }`}>
        {/* Gespeicherte Anhänge */}
        {materialId && items.length > 0 && (
          <div className="p-2 space-y-2">
            {items.map((a) => (
              <div key={a.id} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className={`flex-1 min-w-0 truncate text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{a.filename}</span>
                <span className={`text-[11px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{kb(a.size_bytes)}</span>
                <button type="button" title={t('material.attDownload')} onClick={() => onDownload(a)}
                  className={`p-1.5 rounded-lg ${isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100'}`}>
                  <Download className="w-4 h-4" />
                </button>
                <button type="button" title={t('material.delete')} onClick={() => onDelete(a)}
                  className={`p-1.5 rounded-lg ${isDarkMode ? 'text-red-300/70 hover:text-red-300 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Vorgemerkte Anhänge (neues Material) */}
        {!materialId && staged.length > 0 && (
          <div className="p-2 space-y-2">
            {staged.map((f, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className={`flex-1 min-w-0 truncate text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{f.name}</span>
                <span className={`inline-flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-amber-300/80' : 'text-amber-600'}`}>
                  <Clock className="w-3 h-3" /> {t('material.attPending')}
                </span>
                <button type="button" title={t('material.delete')} onClick={() => removeStaged(i)}
                  className={`p-1.5 rounded-lg ${isDarkMode ? 'text-red-300/70 hover:text-red-300 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Drop-Hinweis */}
        <div className={`flex items-center justify-center gap-2 px-3 py-4 text-xs ${
          dragOver ? 'text-emerald-600 dark:text-emerald-300 font-medium' : (isDarkMode ? 'text-white/40' : 'text-slate-400')
        }`}>
          <Upload className="w-3.5 h-3.5" />
          {busy ? t('common.loading') : dragOver ? t('material.attDropNow')
            : (materialId ? t('material.attDropHint') : t('material.attDropHintNew'))}
        </div>
      </div>
    </div>
  );
};

export default MaterialAttachments;
